import React from 'react'
import { Helmet } from 'react-helmet-async'
import { ServiceType } from '../../types'
import { generateSEOMetadata, SEOMetadata } from '../../utils/seo'

interface SEOHeadProps {
  serviceType?: ServiceType
  customTitle?: string
  customDescription?: string
  customMetadata?: Partial<SEOMetadata>
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  serviceType,
  customTitle,
  customDescription,
  customMetadata
}) => {
  const metadata = generateSEOMetadata(serviceType, customTitle, customDescription)
  const finalMetadata = { ...metadata, ...customMetadata }

  return (
    <Helmet>
      {/* Basic meta tags */}
      <title>{finalMetadata.title}</title>
      <meta name="description" content={finalMetadata.description} />
      <meta name="keywords" content={finalMetadata.keywords.join(', ')} />
      <link rel="canonical" href={finalMetadata.canonicalUrl} />
      
      {/* Open Graph tags */}
      <meta property="og:title" content={finalMetadata.ogTitle || finalMetadata.title} />
      <meta property="og:description" content={finalMetadata.ogDescription || finalMetadata.description} />
      <meta property="og:image" content={finalMetadata.ogImage || ''} />
      <meta property="og:type" content={finalMetadata.ogType || 'website'} />
      <meta property="og:url" content={finalMetadata.canonicalUrl} />
      <meta property="og:site_name" content="Mernaki" />
      
      {/* Twitter Card tags */}
      <meta name="twitter:card" content={finalMetadata.twitterCard || 'summary_large_image'} />
      <meta name="twitter:title" content={finalMetadata.twitterTitle || finalMetadata.title} />
      <meta name="twitter:description" content={finalMetadata.twitterDescription || finalMetadata.description} />
      <meta name="twitter:image" content={finalMetadata.twitterImage || finalMetadata.ogImage || ''} />
      
      {/* Additional SEO tags */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content="Mernaki" />
      <meta name="language" content="en" />
      
      {/* Structured data */}
      {finalMetadata.structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(finalMetadata.structuredData)}
        </script>
      )}
    </Helmet>
  )
}

// Service-specific SEO components
export const HRPageSEO: React.FC<{ customTitle?: string; customDescription?: string }> = ({
  customTitle,
  customDescription
}) => (
  <SEOHead
    serviceType={ServiceType.HR}
    customTitle={customTitle}
    customDescription={customDescription}
  />
)

export const CompliancePageSEO: React.FC<{ customTitle?: string; customDescription?: string }> = ({
  customTitle,
  customDescription
}) => (
  <SEOHead
    serviceType={ServiceType.COMPLIANCE}
    customTitle={customTitle}
    customDescription={customDescription}
  />
)

export const TradePageSEO: React.FC<{ customTitle?: string; customDescription?: string }> = ({
  customTitle,
  customDescription
}) => (
  <SEOHead
    serviceType={ServiceType.TRADE}
    customTitle={customTitle}
    customDescription={customDescription}
  />
)

export const IntelligencePageSEO: React.FC<{ customTitle?: string; customDescription?: string }> = ({
  customTitle,
  customDescription
}) => (
  <SEOHead
    serviceType={ServiceType.INTELLIGENCE}
    customTitle={customTitle}
    customDescription={customDescription}
  />
)

export const MainPageSEO: React.FC<{ customTitle?: string; customDescription?: string }> = ({
  customTitle,
  customDescription
}) => (
  <SEOHead
    customTitle={customTitle}
    customDescription={customDescription}
  />
)